import { useState } from "react"
import { format } from "date-fns"
import { IconTrashOutline18 as Trash } from "nucleo-ui-outline-18"

import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
	Alert,
	AlertDialog,
	IconButton,
	Loader,
	Pill,
} from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { SettingsContainer } from "../../../settings-switches/settings-container"

// Sunset is ~6 months + 1 week out from launch (1 week buffer for the
// v2 development phase). Keep this in one place so the banner copy and
// the v1 response headers stay aligned.
const V1_SUNSET_DATE = new Date("2026-11-20T00:00:00Z")

export function LegacyApiKeysBlock() {
	const apiKeyQuery = api.workspace.listApiKeys.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})

	if (apiKeyQuery.isLoading) {
		return (
			<div className="mt-8 flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	const v1Keys = apiKeyQuery.data ?? []
	if (v1Keys.length === 0) return null

	const sunsetLabel = format(V1_SUNSET_DATE, "MMM d, yyyy")

	return (
		<Accordion type="single" collapsible className="mt-8">
			<AccordionItem value="legacy-keys" className="border-0">
				<AccordionTrigger className="text-sm font-semibold">
					Legacy API keys ({v1Keys.length})
				</AccordionTrigger>
				<AccordionContent>
					<Alert header="Action required" variant="warning">
						These keys use the deprecated v1 API. We will sunset v1 on{" "}
						<strong>{sunsetLabel}</strong>. Migrate to v2 by creating a new API
						key above and updating your integrations. <br />
						<br />
						<a
							href="https://productlane.com/docs/migrating-from-api-v1-to-v2"
							target="_blank"
							rel="noreferrer"
							className="underline underline-offset-2"
						>
							Migration guide ↗
						</a>
					</Alert>
					<div className="mt-3">
						<LegacyApiKeysList keys={v1Keys} onDeleted={apiKeyQuery.refetch} />
					</div>
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	)
}

interface LegacyApiKey {
	keyId: string
	name?: string
	start?: string
	createdAt?: number | string
}

function LegacyApiKeysList({
	keys,
	onDeleted,
}: {
	keys: LegacyApiKey[]
	onDeleted: () => void
}) {
	const [deleteId, setDeleteId] = useState("")
	const deleteMutation = api.workspace.deleteApiKey.useMutation({
		onSuccess: () => {
			toast.success({ title: "Legacy key deleted" })
			onDeleted()
		},
	})

	return (
		<>
			<SettingsContainer>
				<div className="flex flex-col">
					{keys.map((key) => (
						<div
							key={key.keyId}
							className="flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
						>
							<div className="min-w-0 flex-1">
								<div className="flex items-center gap-2 text-sm">
									<span className="truncate font-medium">{key.name}</span>
									<Pill variant="dark-gray" size="sm">
										v1
									</Pill>
									<span className="font-mono text-xs text-secondary">
										{key.start}…
									</span>
								</div>
								<div className="text-xs text-secondary">
									{key.createdAt &&
										`created ${format(new Date(key.createdAt), "MMM d, yyyy")}`}
								</div>
							</div>
							<IconButton
								Icon={Trash}
								onClick={() => setDeleteId(key.keyId)}
								disabled={
									deleteMutation.isLoading &&
									deleteMutation.variables?.keyId === key.keyId
								}
							/>
						</div>
					))}
				</div>
			</SettingsContainer>
			<AlertDialog
				isOpen={!!deleteId}
				onOpenChange={() => setDeleteId("")}
				title="Delete legacy key?"
				description="The key will stop working immediately. Any integrations using it will break."
				confirmText="Delete"
				onCancel={() => setDeleteId("")}
				onConfirm={() => {
					deleteMutation.mutate({ keyId: deleteId })
					setDeleteId("")
				}}
			/>
		</>
	)
}
